import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { maxLength } from 'utils/validation-utils'
import { validationContext } from 'validx'
import { Id } from 'types/definitions'

/**
 * View Model for the Remove Client Team dialog.
 * @extends ModalViewModel
 */
export default class RemoveClientTeamViewModel extends ModalViewModel {
  /**
   * Holds the maximum length of each field.
   */
  private readonly fieldLengths = {
    reason: { max: 100 },
  }

  /**
   * Validation context for the view model.
   */
  validation = validationContext(this, {
    reason: [maxLength(this.fieldLengths.reason.max)],
  })

  /**
   * Helper text for reason field.
   */
  reasonHelpText =
    'Why are you removing the team? This text is public and can be seen by both Client and Pros.'

  /**
   * The reason for the removal of the client team.
   */
  @observable
  reason = ''

  constructor(
    private job: Job,
    private jobStore: JobStore,
    private newAlert: AlertFactory,
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * Getter for the current job client team.
   */
  @computed
  get currentClientTeam() {
    return this.job.ownerTeam
  }

  /**
   * Getter for the current job client team name.
   */
  @computed
  get currentClientTeamName() {
    return this.job.ownerTeam!.name
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.reason = ''

    this.validation.reset()
  }

  /**
   * Removes client team from Job.
   */
  removeClientTeam = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Removing job from client team',
        `Removing ${this.job.ownerTeam?.name}...`,
      )
      .autoDismiss()
      .show()

    await this.jobStore.removeClientTeamAsAdmin(
      Id.assert(this.job.id),
      this.reason,
    )

    alert
      .withState(AlertState.Success)
      .saying('Success!', `Removed job from ${this.job.ownerTeam?.name}.`)
      .autoDismiss()

    this.close()
  })

  /**
   * Shows the Remove Client Team Dialog.
   */
  show() {
    if (this.showing) {
      return
    }

    super.show()
  }
}
